/* eslint-disable max-lines */
import { useQuery } from '@apollo/client/react';
import { GET_ARBEITSZEITEN_BY_DATE_AND_STANDORT_ID } from './graphql/queries';
import {
  Abteilung,
  Abwesenheit,
  Antrag,
  Arbeitsbereich,
  Arbeitszeit,
  Dienstgang,
  Mitarbeiter,
  Pause,
  Puenktlichkeit,
  Rechtshinweis,
  Zuschlag,
  Audit,
  ArbeitszeitAuditAenderung,
  ArbeitszeitAuditManager,
  Dienstplanuebernahme,
  mapAbwesenheitsgrund,
  toArbeitszeitenPausenart,
  toBackgroundColor,
  toMarkierung,
} from '../entities';
import { handleGraphQLErrors } from '../../../utils/errors/handleGraphQLErrors';
import {
  ArbeitszeitenPauseneinstellung,
  Arbeitszeiten,
  ArbeitszeitenByDatum,
  Audit as AuditGql,
  AuditResult,
  GetArbeitszeitenByDateAndStandortIdQuery,
  GetArbeitszeitenByDateAndStandortIdQueryVariables,
} from '../../../gql/graphql';
import { getDateOrNull } from '../../../utils/helperFunctions';
import { Termin, toTerminWiederholung } from '../entities/Termin';
import { toArbeitszeitAuditAenderungsTyp } from '../entities/ArbeitszeitAuditAenderungsTyp';
import { useToDos, UseToDosResult } from './useToDos';
import { Konflikt } from '../components/Table/ToDoTable/entities/Konflikt';
import { Arbeitszeit as ToDoArbeitszeit } from '../components/Table/ToDoTable/entities/Arbeitszeit';

function getDateOrUndefined(value?: string | null): Date | undefined {
  return getDateOrNull(value) ?? undefined;
}

function calculatePausendauer(arbeitszeit: Arbeitszeiten) {
  let pausendauer = 0;
  for (let i = 0; i < arbeitszeit.pausen.length; i++) {
    if (arbeitszeit.pausen[i].dauer !== null) {
      pausendauer += arbeitszeit.pausen[i].dauer ?? 0;
    }
  }
  return pausendauer;
}

function calculatePuenktlichkeit(arbeitszeit: Arbeitszeiten) {
  let puenktlichkeit = new Puenktlichkeit();

  if (
    arbeitszeit.puenktlichkeit !== null &&
    arbeitszeit.puenktlichkeit !== undefined
  ) {
    puenktlichkeit = new Puenktlichkeit(
      arbeitszeit.puenktlichkeit.abweichungBeginn ?? undefined,
      arbeitszeit.puenktlichkeit.abweichungEnde ?? undefined,
    );
  }
  return puenktlichkeit;
}

function mapDienstgaenge(arbeitszeit: Arbeitszeiten): Dienstgang[] {
  return arbeitszeit.dienstgaenge.map((dienstgang) => {
    return {
      id: dienstgang.id ?? undefined,
      beginn: getDateOrUndefined(dienstgang.beginn),
      ende: getDateOrUndefined(dienstgang.ende),
      dauer: dienstgang.dauer ?? undefined,
    };
  });
}

function mapPausen(arbeitszeit: Arbeitszeiten): Pause[] {
  return arbeitszeit.pausen.map((pause) => {
    return {
      id: pause.id ?? undefined,
      beginn: getDateOrUndefined(pause.beginn),
      ende: getDateOrUndefined(pause.ende),
      dauer: pause.dauer ?? undefined,
      art: toArbeitszeitenPausenart(pause.art),
      bezahlt: pause.bezahlt,
    };
  });
}

function mapRechtshinweise(arbeitszeit: Arbeitszeiten): Rechtshinweis[] {
  return arbeitszeit.rechtshinweise.map((rechtshinweis) => {
    return {
      name: rechtshinweis.name,
      art: rechtshinweis.art,
      backgroundcolor: toBackgroundColor(rechtshinweis.backgroundcolor),
    };
  });
}

function mapAntraege(arbeitszeit: Arbeitszeiten): Antrag[] {
  return (arbeitszeit.antraege ?? []).map((antrag) => {
    return new Antrag(
      antrag.id ?? '',
      antrag.status ?? undefined,
      antrag.arbeitszeitId ?? '',
      antrag.offen ?? false,
      antrag.bemerkungAntragsteller ?? '',
      antrag.bemerkungVorgesetzter ?? '',
      getDateOrUndefined(antrag.datum),
      getDateOrUndefined(antrag.aenderungsdatum),
      getDateOrUndefined(antrag.erstellungsdatum),
      new Arbeitsbereich(
        antrag.arbeitsbereich?.name ?? '',
        antrag.arbeitsbereich?.id ?? '',
        toMarkierung(antrag.arbeitsbereich?.markierung ?? ''),
        [],
      ),
      new Mitarbeiter(
        antrag.mitarbeiter?.vorname ?? '',
        antrag.mitarbeiter?.nachname ?? '',
        antrag.mitarbeiter?.id ?? '',
        false,
        antrag.mitarbeiter?.bildUrl ?? undefined,
      ),
      {
        time:
          getDateOrUndefined(antrag.beginn) ??
          getDateOrUndefined(arbeitszeit.beginn),
        isChangeRequested: !!antrag.beginn,
      },
      {
        time:
          getDateOrUndefined(antrag.ende) ??
          getDateOrUndefined(arbeitszeit.ende),
        isChangeRequested: !!antrag.ende,
      },
    );
  });
}

function isAuditGql(audit: AuditResult | null | undefined): audit is AuditGql {
  return !!audit && audit.__typename === 'Audit';
}

function mapAudit(arbeitszeit: Arbeitszeiten): Audit[] {
  return (arbeitszeit.audit ?? []).filter(isAuditGql).map((audit) => {
    return new Audit(
      audit.id ?? '',
      audit.typ ?? '',
      toArbeitszeitAuditAenderungsTyp(audit.aenderungsTyp),
      audit.aenderungsdatum ? new Date(audit.aenderungsdatum) : new Date(),
      audit.geaendertVon ?? '',
      audit.geaendertVonEmail ?? '',
      audit.manager
        ? new ArbeitszeitAuditManager(
            audit.manager.email ?? '',
            audit.manager.vorname ?? '',
            audit.manager.nachname ?? '',
          )
        : undefined,
      (audit.aenderungen ?? []).map(
        (aenderung) =>
          new ArbeitszeitAuditAenderung(
            aenderung.attribut ?? '',
            aenderung.nachher ?? '',
            aenderung.vorher ?? '',
          ),
      ),
    );
  });
}

function isArbeitszeit(
  arbeitszeit: Arbeitszeit | Antrag,
): arbeitszeit is Arbeitszeit {
  return arbeitszeit instanceof Arbeitszeit;
}

function isAntrag(arbeitszeit: Arbeitszeit | Antrag): arbeitszeit is Antrag {
  return arbeitszeit instanceof Antrag;
}

function isOffen(arbeitszeit: Arbeitszeit | Antrag): boolean {
  if (isArbeitszeit(arbeitszeit)) {
    return (
      (!arbeitszeit.start || !arbeitszeit.ende) && !arbeitszeit.abwesenheit
    );
  }

  if (isAntrag(arbeitszeit) && arbeitszeit.offen) {
    return arbeitszeit.status !== undefined;
  }
  return false;
}

export type ArbeitszeitenResult = {
  abgeschlosseneArbeitszeiten: Arbeitszeit[];
  todos: UseToDosResult;
  dienstplanuebernahmen: Dienstplanuebernahme[];
  offeneArbeitszeiten: (Arbeitszeit | Antrag)[];
  error?: string;
  loading: boolean;
};

// eslint-disable-next-line max-lines-per-function
export function useArbeitszeiten(
  datum: Date,
  standortId: string,
): ArbeitszeitenResult {
  const parsedDate = datum.toISOString().slice(0, 10);
  const { data, loading } = useQuery<
    GetArbeitszeitenByDateAndStandortIdQuery,
    GetArbeitszeitenByDateAndStandortIdQueryVariables
  >(GET_ARBEITSZEITEN_BY_DATE_AND_STANDORT_ID, {
    variables: {
      datum: parsedDate,
      standortId: standortId,
    },
    fetchPolicy: 'no-cache',
  });

  const toDos = useToDos(parsedDate, standortId);

  if (loading || toDos.loading) {
    return {
      abgeschlosseneArbeitszeiten: [],
      todos: toDos,
      dienstplanuebernahmen: [],
      offeneArbeitszeiten: [],
      loading: true,
    };
  }

  if (!data) {
    return {
      error: 'Keine Daten erhalten',
      abgeschlosseneArbeitszeiten: [],
      todos: {
        toDos: [],
        loading: false,
      },
      offeneArbeitszeiten: [],
      dienstplanuebernahmen: [],
      loading: false,
    };
  }

  const error = handleGraphQLErrors(data.arbeitszeiten);

  if (error) {
    return {
      error: error.message,
      abgeschlosseneArbeitszeiten: [],
      todos: {
        toDos: [],
        loading: false,
      },
      offeneArbeitszeiten: [],
      dienstplanuebernahmen: [],
      loading: false,
    };
  }

  const konfliktArbeitszeitIds = toDos.toDos
    .filter((a) => a._type === 'konflikt')
    .flatMap((a) => {
      const konflikt = a as Konflikt;
      const ids = [konflikt.rootArbeitszeit.id];
      if (konflikt.conflictCause instanceof ToDoArbeitszeit) {
        ids.push(konflikt.conflictCause.id);
      }
      return ids;
    });

  const zeiten = data.arbeitszeiten as ArbeitszeitenByDatum;

  const arbeitszeiten = zeiten.arbeitszeiten.map((arbeitszeit) => {
    return createArbeitszeit(arbeitszeit);
  });

  if (data.antraegeByStandortId.__typename !== 'ArbeitszeitAntraege') {
    throw new Error('Unexpected typename');
  }

  const abgeschlosseneArbeitszeiten = arbeitszeiten.filter((arbeitszeit) => {
    return (
      !isOffen(arbeitszeit) &&
      (!arbeitszeit.antraege ||
        arbeitszeit.antraege.every((antrag) => !antrag.offen))
    );
  });

  const dienstplanuebernahmen = data.dienstplanuebernahmen.map(
    // eslint-disable-next-line complexity
    (dienstplanuebernahme) =>
      new Dienstplanuebernahme(
        dienstplanuebernahme.id,
        dienstplanuebernahme.schichtId,
        getDateOrUndefined(dienstplanuebernahme.beginn) ?? new Date(),
        dienstplanuebernahme.pause,
        dienstplanuebernahme.dauer,
        new Mitarbeiter(
          dienstplanuebernahme.mitarbeiter.vorname,
          dienstplanuebernahme.mitarbeiter.nachname,
          dienstplanuebernahme.mitarbeiter.id ?? '',
          dienstplanuebernahme.mitarbeiter.extern,
          dienstplanuebernahme.mitarbeiter.bildUrl ?? undefined,
        ),
        new Arbeitsbereich(
          dienstplanuebernahme.arbeitsbereich.name,
          dienstplanuebernahme.arbeitsbereich.id ?? '',
          toMarkierung(dienstplanuebernahme.arbeitsbereich.markierung),
          [],
        ),
        dienstplanuebernahme.termin
          ? new Termin(
              dienstplanuebernahme.termin.titel ?? undefined,
              toTerminWiederholung(dienstplanuebernahme.termin.wiederholung),
              dienstplanuebernahme.termin.id ?? undefined,
              dienstplanuebernahme.termin.bildUrl ?? undefined,
              dienstplanuebernahme.termin.bildName ?? undefined,
              dienstplanuebernahme.termin.beschreibung ?? undefined,
              undefined,
              undefined,
              dienstplanuebernahme.termin.markierung ?? undefined,
            )
          : undefined,
        getDateOrUndefined(dienstplanuebernahme.ende),
      ),
  );

  const antraegeResult = data.antraegeByStandortId.antraege;

  const antraege = antraegeResult.map((antrag) => {
    return new Antrag(
      antrag.id ?? '',
      antrag.status ?? undefined,
      antrag.arbeitszeitId ?? '',
      antrag.offen ?? false,
      antrag.bemerkungAntragsteller ?? '',
      antrag.bemerkungVorgesetzter ?? '',
      getDateOrUndefined(antrag.datum),
      getDateOrUndefined(antrag.aenderungsdatum),
      getDateOrUndefined(antrag.erstellungsdatum),
      new Arbeitsbereich(
        antrag.arbeitsbereich?.name ?? '',
        antrag.arbeitsbereich?.id ?? '',
        toMarkierung(antrag.arbeitsbereich?.markierung ?? ''),
        [],
      ),
      new Mitarbeiter(
        antrag.mitarbeiter?.vorname ?? '',
        antrag.mitarbeiter?.nachname ?? '',
        antrag.mitarbeiter?.id ?? '',
        false,
        antrag.mitarbeiter?.bildUrl ?? undefined,
      ),
      {
        time: getDateOrUndefined(antrag.beginn),
        isChangeRequested: !!antrag.beginn,
      },
      {
        time: getDateOrUndefined(antrag.ende),
        isChangeRequested: !!antrag.ende,
      },
    );
  });
  const offeneArbeitszeiten: (Arbeitszeit | Antrag)[] = [
    ...arbeitszeiten.filter((arbeitszeit) => {
      return (
        !arbeitszeit.abwesenheit &&
        (!arbeitszeit.start ||
          (!arbeitszeit.ende && isOffen(arbeitszeit)) ||
          (arbeitszeit.antraege ?? []).some((antrag) => antrag.offen))
      );
    }),
    ...antraege.filter((antrag) => {
      return antrag.offen;
    }),
  ];

  return {
    todos: toDos,
    abgeschlosseneArbeitszeiten: abgeschlosseneArbeitszeiten.filter(
      (az) => konfliktArbeitszeitIds.includes(az.id) === false,
    ),
    dienstplanuebernahmen,
    offeneArbeitszeiten: offeneArbeitszeiten,
    loading: false,
  };
}

// eslint-disable-next-line max-lines-per-function, complexity
function createArbeitszeit(arbeitszeit: Arbeitszeiten) {
  const pausendauer = calculatePausendauer(arbeitszeit);
  const puenktlichkeit = calculatePuenktlichkeit(arbeitszeit);
  const dienstgaenge: Dienstgang[] = mapDienstgaenge(arbeitszeit);
  const bemerkung = arbeitszeit.bemerkung ? arbeitszeit.bemerkung : '';
  const pausen: Pause[] = mapPausen(arbeitszeit);
  const zuschlaege: Zuschlag[] = [];
  const antraege: Antrag[] = mapAntraege(arbeitszeit);
  const rechtshinweise: Rechtshinweis[] = mapRechtshinweise(arbeitszeit);
  const audits: Audit[] = arbeitszeit.audit ? mapAudit(arbeitszeit) : [];
  let abwesenheit: Abwesenheit | undefined;

  if (arbeitszeit.abwesenheit) {
    abwesenheit = new Abwesenheit(
      arbeitszeit.abwesenheit.id,
      mapAbwesenheitsgrund(arbeitszeit.abwesenheit.grund),
      arbeitszeit.abwesenheit.dauer,
    );
  }
  if (arbeitszeit.zuschlaege !== null) {
    const tmp = arbeitszeit.zuschlaege.map((zuschlag) => {
      if (zuschlag.betrag.__typename === 'arbeitszeiten_zuschlaege_betrag') {
        return new Zuschlag(
          zuschlag.dauer,
          zuschlag.name,
          zuschlag.prozent,
          zuschlag.betrag.betrag,
        );
      }
      return new Zuschlag(zuschlag.dauer, zuschlag.name, zuschlag.prozent);
    });
    zuschlaege.push(...tmp);
  }

  let termin: Termin | undefined;

  if (arbeitszeit.termin) {
    termin = new Termin(
      arbeitszeit.termin.titel ?? undefined,
      toTerminWiederholung(arbeitszeit.termin.wiederholung),
      arbeitszeit.termin.id ?? undefined,
      arbeitszeit.termin.bildUrl ?? undefined,
      arbeitszeit.termin.bildName ?? undefined,
      arbeitszeit.termin.beschreibung ?? undefined,
      undefined,
      undefined,
      arbeitszeit.termin.markierung ?? undefined,
    );
  }

  return new Arbeitszeit(
    arbeitszeit.id ?? '',
    new Mitarbeiter(
      arbeitszeit.mitarbeiter.vorname,
      arbeitszeit.mitarbeiter.nachname,
      arbeitszeit.mitarbeiter.id ?? '',
      arbeitszeit.mitarbeiter.extern,
      arbeitszeit.mitarbeiter.bildUrl ?? undefined,
    ),
    new Arbeitsbereich(
      arbeitszeit.arbeitsbereich.name,
      arbeitszeit.arbeitsbereich.id ?? '',
      arbeitszeit.arbeitsbereich.markierung,
      (arbeitszeit.arbeitsbereich.abteilungen ?? []).map(
        (abteilung) =>
          new Abteilung(abteilung.name, abteilung.id, abteilung.markierung),
      ),
    ),
    new Abteilung(
      arbeitszeit.arbeitsbereich.name,
      arbeitszeit.arbeitsbereich.id ?? '',
      arbeitszeit.arbeitsbereich.markierung,
    ),
    arbeitszeit.dauer ?? 0,
    pausendauer,
    puenktlichkeit,
    pausen,
    arbeitszeit.pauseneinstellung ?? ArbeitszeitenPauseneinstellung.Automatisch,
    dienstgaenge,
    [],
    rechtshinweise,
    '',
    getDateOrNull(arbeitszeit.beginn) ?? undefined,
    getDateOrNull(arbeitszeit.ende) ?? undefined,
    getDateOrNull(arbeitszeit.kommen) ?? undefined,
    getDateOrNull(arbeitszeit.gehen) ?? undefined,
    abwesenheit,
    bemerkung,
    zuschlaege,
    antraege,
    audits,
    termin,
    arbeitszeit.geaendertVon ?? undefined,
    getDateOrUndefined(arbeitszeit.aenderungsdatum),
    getDateOrNull(arbeitszeit.datum) ?? undefined,
  );
}
